import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { Capacitor } from '@capacitor/core';
import { api } from '../../convex/_generated/api';
import { useAuth } from '@/context/AuthContext';
import { isAiAllowed, shouldPromptForConsent, type AiConsent } from '@/lib/ai-consent';
import AiConsentDialog from '@/components/ai/AiConsentDialog';

type AiConsentContextType = {
  consent: AiConsent | null;
  aiEnabled: boolean;
  isNative: boolean;
  shouldPrompt: boolean;
  sheetOpen: boolean;
  openSheet: () => void;
  decide: (status: 'granted' | 'declined') => Promise<void>;
};

const AiConsentContext = createContext<AiConsentContextType | undefined>(undefined);

const IS_NATIVE = Capacitor.isNativePlatform();

export const AiConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const setConsent = useMutation(api.aiConsent.set);
  const [reopened, setReopened] = useState(false);

  const consent = user?.aiConsent ?? null;
  // Mirrors the server guard: off native there is no gate, so AI stays on.
  const aiEnabled = isAiAllowed(consent, IS_NATIVE);
  const shouldPrompt = shouldPromptForConsent(consent, IS_NATIVE);

  // The launch sheet opens on its own; `reopened` covers the Settings toggle.
  const sheetOpen = Boolean(user) && (shouldPrompt || reopened);

  const openSheet = useCallback(() => setReopened(true), []);

  const decide = useCallback(
    async (status: 'granted' | 'declined') => {
      await setConsent({ status });
      setReopened(false);
    },
    [setConsent]
  );

  const value = useMemo(
    () => ({ consent, aiEnabled, isNative: IS_NATIVE, shouldPrompt, sheetOpen, openSheet, decide }),
    [consent, aiEnabled, shouldPrompt, sheetOpen, openSheet, decide]
  );

  return (
    <AiConsentContext.Provider value={value}>
      {children}
      <AiConsentDialog />
    </AiConsentContext.Provider>
  );
};

export const useAiConsent = (): AiConsentContextType => {
  const ctx = useContext(AiConsentContext);
  if (!ctx) throw new Error('useAiConsent must be used within AiConsentProvider');
  return ctx;
};
